'use client';

import {
  Box,
  Card,
  Checkbox,
  Divider,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material';
import type { DataCatalog } from '@repo/api-gw-sdk';
import { DataCatalogType } from '@repo/api-gw-sdk';
import { useState, useMemo } from 'react';

import { Loader } from '@/components/layout/loading';
import { BrowserGrid } from '@/components/shared/browserGrid';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { RenameEntityName } from '@/components/wizard/renameEntityName';
import { useDAL } from '@/contexts/useDAL';

import { RoutingPolicyPanel } from './RoutingPolicyPanel';
import type { ZeroEtlPolicyEditState } from './ZeroEtlPolicyCreationFlow';

const CATALOG_TYPE_IMAGES: Record<DataCatalogType, string> = {
  [DataCatalogType.DATA_CATALOG_TYPE_GLUE_CATALOG]:
    '/statics/images/lakehouse/Glue.png',
  [DataCatalogType.DATA_CATALOG_TYPE_BIGLAKE_CATALOG]:
    '/statics/images/lakehouse/BigLake.png',
  [DataCatalogType.DATA_CATALOG_TYPE_ICEBERG_REST_CATALOG]:
    '/statics/images/lakehouse/Iceberg.png',
};

export const ZeroEtlPolicyCatalogSelection = (
  props: StepProps<ZeroEtlPolicyEditState>
) => {
  const dal = useDAL();
  const { data: catalogsData, isLoading } = dal.lakehouse.useListDataCatalogs(
    props.wizardState.workspaceId
  );
  const [searchQuery, setSearchQuery] = useState('');

  const catalogs = useMemo(() => catalogsData?.catalogs ?? [], [catalogsData]);

  const filteredCatalogs = useMemo(
    () =>
      catalogs.filter((cat) =>
        cat.name.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [catalogs, searchQuery]
  );

  const toggleCatalog = (catalogId: string) => {
    const current = props.wizardState.catalogIds;
    const updated = current.includes(catalogId)
      ? current.filter((cid) => cid !== catalogId)
      : [...current, catalogId];
    props.setWizardState({ ...props.wizardState, catalogIds: updated });
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <StepContainer
      stepperLabels={props.stepperLabels}
      stepperIndex={2}
      onBackClick={props.back}
      canGoNext={() => props.wizardState.catalogIds.length > 0}
      nextButtonDisabledTooltip='Select at least one catalog'
      onNextClick={async () => {
        await props.finish();
      }}
      nextButtonText='Save'
      sx={{ padding: '0px' }}
      navigationComponent={
        <RenameEntityName
          label='Zero-ETL policy name'
          name={props.wizardState.name}
          onSubmit={(name) =>
            props.setWizardState((state) => ({ ...state, name }))
          }
        />
      }
    >
      <Stack
        direction='row'
        gap='48px'
        justifyContent='space-between'
        className='mx-[min(100px,5vw)] mt-[min(60px,3vw)]'
        flexGrow={1}
        minHeight={0}
      >
        <Stack flexGrow={1} minHeight={0} gap='24px' minWidth={0}>
          <Typography variant='h3'>Catalog selection</Typography>
          <Divider />
          <OutlinedInput
            placeholder='Search catalogs...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ maxWidth: '400px' }}
            startAdornment={
              <i
                className='material-symbols-search'
                style={{
                  color: 'var(--mui-palette-text-secondary)',
                  marginRight: '8px',
                }}
              />
            }
          />
          {filteredCatalogs.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              {searchQuery
                ? 'No catalogs match your search.'
                : 'No catalogs available in this workspace.'}
            </Typography>
          ) : (
            <BrowserGrid columns={2} gap='24px'>
              {filteredCatalogs.map((catalog) => (
                <CatalogCard
                  key={catalog.id}
                  catalog={catalog}
                  selected={props.wizardState.catalogIds.includes(catalog.id)}
                  onToggle={() => toggleCatalog(catalog.id)}
                />
              ))}
            </BrowserGrid>
          )}
        </Stack>
        <Stack direction='row' gap='32px' flexShrink={0} width='600px'>
          <Divider orientation='vertical' flexItem />
          <RoutingPolicyPanel
            state={props.wizardState}
            onChange={(updates) =>
              props.setWizardState((state) => ({ ...state, ...updates }))
            }
          />
        </Stack>
      </Stack>
    </StepContainer>
  );
};

const CatalogCard = ({
  catalog,
  selected,
  onToggle,
}: {
  catalog: DataCatalog;
  selected: boolean;
  onToggle: () => void;
}) => {
  const imageSrc = CATALOG_TYPE_IMAGES[catalog.catalogType] ?? '';

  return (
    <Card
      onClick={onToggle}
      sx={{
        backgroundColor: 'var(--mui-palette-background-paper)',
        border: selected
          ? '2px solid var(--mui-palette-primary-main)'
          : '1px solid var(--mui-palette-divider)',
        borderRadius: '16px',
        padding: '16px',
        cursor: 'pointer',
        minHeight: '80px',
        boxShadow: 'none',
        '&:hover': {
          borderColor: 'var(--mui-palette-primary-main)',
        },
      }}
    >
      <Stack direction='row' alignItems='center' gap='16px'>
        <Checkbox checked={selected} sx={{ padding: '0px' }} />
        {imageSrc && (
          <Box
            component='img'
            src={imageSrc}
            alt={catalog.catalogType}
            sx={{ width: '32px', height: '32px', objectFit: 'contain' }}
          />
        )}
        <Stack gap='4px'>
          <Typography variant='h4' fontWeight='bold'>
            {catalog.name}
          </Typography>
          <CatalogDetails catalog={catalog} />
        </Stack>
      </Stack>
    </Card>
  );
};

const CatalogDetails = ({ catalog }: { catalog: DataCatalog }) => {
  switch (catalog.catalogType) {
    case DataCatalogType.DATA_CATALOG_TYPE_GLUE_CATALOG: {
      const accountId = catalog.catalogExternalId?.split(':')[0];
      const region = catalog.glueProperties?.region;
      return (
        <Stack gap='2px'>
          {accountId && (
            <Typography variant='body2' fontWeight='bold'>
              {accountId}
            </Typography>
          )}
          {region && (
            <Typography variant='body2' fontWeight='bold'>
              {region}
            </Typography>
          )}
        </Stack>
      );
    }
    case DataCatalogType.DATA_CATALOG_TYPE_BIGLAKE_CATALOG: {
      const props = catalog.biglakeProperties;
      return (
        <Stack gap='2px'>
          {props?.catalogId && (
            <Typography variant='body2' fontWeight='bold'>
              {props.catalogId}
            </Typography>
          )}
          {props?.location && (
            <Typography variant='body2' fontWeight='bold'>
              {props.location}
            </Typography>
          )}
        </Stack>
      );
    }
    case DataCatalogType.DATA_CATALOG_TYPE_ICEBERG_REST_CATALOG: {
      const props = catalog.icebergRestProperties;
      return (
        <Stack gap='2px'>
          {props?.uri && (
            <Typography variant='body2' fontWeight='bold'>
              {props.uri}
            </Typography>
          )}
          {props?.warehouse && (
            <Typography variant='body2' fontWeight='bold'>
              {props.warehouse}
            </Typography>
          )}
        </Stack>
      );
    }
    default:
      return null;
  }
};
